/**
 * Pending human-input requests, as the inbox and the popup render them
 * (prompt.md §23, §78).
 *
 * WHY every view is derived from the server's durable pending list instead of a
 * local queue: a question is answered once, on whichever device reads it first.
 * Deriving the list *and* the popup from that one list means a client that was
 * never open, a client that was answered elsewhere, and a client that reconnected
 * after a restart all converge on the same answer instead of holding a prompt the
 * run has already moved past. Nothing is answered without an explicit submit.
 */

import { FormSchema, formFieldProtection, formRevision as formRevisionOf, type FormDefinition, type FormField } from "@dayvise/contracts";

/** How long before expiry a request is flagged, so a user is not surprised by a 409. */
export const INPUT_REQUEST_EXPIRING_MS = 2 * 60_000;

export type InboxRequestState = "pending" | "expiring" | "expired";

/** The subset of a listed input request this module needs. */
export interface InboxRequestSource {
  readonly requestId: string;
  readonly runId: string;
  readonly conversationId: string;
  readonly branchId: string;
  readonly questions?: readonly unknown[];
  readonly form?: unknown;
  readonly formRevision?: string | null;
  readonly expiresAt?: string | null;
}

export interface InboxRequestView {
  readonly requestId: string;
  readonly runId: string;
  readonly conversationId: string;
  readonly branchId: string;
  readonly state: InboxRequestState;
  /** What the agent asked for, shown only inside the authenticated in-app surface. */
  readonly title: string;
  readonly detail: string;
  /** The declared form, already validated, or `null` for free-form questions. */
  readonly form: FormDefinition | null;
  readonly questions: readonly unknown[];
  /** Revision the answer echoes; the server refuses a submission from an older one. */
  readonly formRevision: string | null;
  readonly expiresAt: string | null;
  readonly remainingLabel: string | null;
  /** False once expired: the server refuses a late answer, so the control is disabled. */
  readonly answerable: boolean;
  /** Fields the agent marked, or that look, like credentials. */
  readonly credentialFieldCount: number;
}

function parseForm(form: unknown): FormDefinition | null {
  if (form === null || form === undefined) return null;
  const parsed = FormSchema.safeParse(form);
  return parsed.success ? parsed.data : null;
}

function credentialFields(fields: readonly FormField[]): number {
  return fields.filter((field) => formFieldProtection(field) !== "ordinary").length;
}

export function formatInboxRemaining(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
  }
  return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, "0")}s` : `${seconds}s`;
}

/** One listed request, with its expiry state resolved against server-supplied `now`. */
export function inboxRequestView(request: InboxRequestSource, now: number): InboxRequestView {
  const form = parseForm(request.form);
  const questions = Array.isArray(request.questions) ? request.questions : [];
  const expiresAt = typeof request.expiresAt === "string" ? request.expiresAt : null;
  const expiresMs = expiresAt === null ? null : Date.parse(expiresAt);
  const remainingMs = expiresMs === null || Number.isNaN(expiresMs) ? null : expiresMs - now;
  const state: InboxRequestState =
    remainingMs === null ? "pending" : remainingMs <= 0 ? "expired" : remainingMs <= INPUT_REQUEST_EXPIRING_MS ? "expiring" : "pending";
  const fields = form === null ? [] : form.fields;
  const credentials = credentialFields(fields);
  const fieldDetail = `${fields.length} field${fields.length === 1 ? "" : "s"}${credentials > 0 ? `, ${credentials} credential${credentials === 1 ? "" : "s"}` : ""}`;
  const questionDetail = `${questions.length} question${questions.length === 1 ? "" : "s"}`;
  const declaredRevision = typeof request.formRevision === "string" ? request.formRevision : null;
  return {
    requestId: request.requestId,
    runId: request.runId,
    conversationId: request.conversationId,
    branchId: request.branchId,
    state,
    title: form?.title ?? "The agent is asking a question",
    detail: form === null ? questionDetail : fieldDetail,
    form,
    questions,
    formRevision: form === null ? null : declaredRevision ?? formRevisionOf(form, request.requestId),
    expiresAt,
    remainingLabel: remainingMs === null || state === "expired" ? null : `Expires in ${formatInboxRemaining(remainingMs)}`,
    answerable: state !== "expired",
    credentialFieldCount: credentials,
  };
}

/**
 * Every pending request, soonest expiry first and expired last.
 *
 * Duplicate rows for one request id collapse to a single entry: the server can
 * report the same request from both its tool-call view and its durable row, and a
 * user must never be shown the same question twice or counted twice in a badge.
 */
export function buildInboxViews(requests: readonly InboxRequestSource[], now: number): readonly InboxRequestView[] {
  const seen = new Set<string>();
  const views: InboxRequestView[] = [];
  for (const request of requests) {
    if (seen.has(request.requestId)) continue;
    seen.add(request.requestId);
    views.push(inboxRequestView(request, now));
  }
  const expiresMs = (view: InboxRequestView) => (view.expiresAt === null ? Number.POSITIVE_INFINITY : Date.parse(view.expiresAt));
  return views
    .map((view, index) => ({ view, index }))
    .sort((left, right) => {
      const leftExpired = left.view.state === "expired" ? 1 : 0;
      const rightExpired = right.view.state === "expired" ? 1 : 0;
      if (leftExpired !== rightExpired) return leftExpired - rightExpired;
      const leftExpiry = expiresMs(left.view);
      const rightExpiry = expiresMs(right.view);
      if (leftExpiry !== rightExpiry) return leftExpiry < rightExpiry ? -1 : 1;
      return left.index - right.index;
    })
    .map((entry) => entry.view);
}

export function answerableCount(views: readonly InboxRequestView[]): number {
  return views.filter((view) => view.answerable).length;
}

export function inboxHeadline(views: readonly InboxRequestView[]): string {
  const pending = answerableCount(views);
  if (pending === 0) return "No questions are waiting for you.";
  return pending === 1 ? "1 question is waiting for an answer." : `${pending} questions are waiting for an answer.`;
}

/**
 * The one request the popup shows, or `null`.
 *
 * A dismissed request stays dismissed on this client: "later" must not mean "ask
 * again in ten seconds". Answering it anywhere removes it from the listed set,
 * which is what closes the popup on every other connected client.
 */
export function nextPopupRequest(
  views: readonly InboxRequestView[],
  dismissed: readonly string[],
): InboxRequestView | null {
  return views.find((view) => view.answerable && !dismissed.includes(view.requestId)) ?? null;
}

export type NotificationPermissionStatus = "unsupported" | "default" | "granted" | "denied";

/** The slice of the Notification API this module reads, so tests inject a fake. */
export interface NotificationApiLike {
  readonly permission: string;
  requestPermission?: () => Promise<string>;
}

export function notificationPermissionStatus(api: NotificationApiLike | null | undefined): NotificationPermissionStatus {
  if (!api || typeof api.permission !== "string") return "unsupported";
  if (api.permission === "granted") return "granted";
  if (api.permission === "denied") return "denied";
  return "default";
}

/**
 * What the notification control says, honestly.
 *
 * The in-app inbox is the fallback in every case, and no wording here may imply a
 * closed browser can be reached: without a push subscription the platform only
 * reaches a page that is still open.
 */
export function notificationPermissionLabel(status: NotificationPermissionStatus): string {
  switch (status) {
    case "granted":
      return "Desktop notifications are on while Dayvise is open in a tab.";
    case "denied":
      return "This browser has notifications blocked, so questions appear in the in-app inbox instead.";
    case "unsupported":
      return "This client has no desktop notification support, so questions appear in the in-app inbox instead.";
    default:
      return "Turn on desktop notifications to be told about a question that arrives while another tab is open. Questions always appear in the in-app inbox.";
  }
}

/** A closed tab is never promised OS popup delivery without a push subscription. */
export const INBOX_DELIVERY_LIMIT = "A closed tab cannot be reached without a persistent push subscription; reopen Dayvise and pending questions are still here.";
